import { useEffect, useMemo, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch } from 'app/hooks';
import {
  projectBoardApi,
  useGetAhuTunnelsQuery,
} from 'app/services/projectBoardApi';
import { compareTunnelAddress } from 'pages/ahu-board/addressing';
import { ahuTunnelAddress, type IAhuTunnel } from 'models';

/** One "Sections" pane line: "{tunnelIndex}   {name}[: {tag}]" (B1.4). */
export interface AhuSectionLine {
  index: number | string;
  name: string;
  /** Pre-rendered `: {tag}` suffix or '' (parity 143). */
  tag: string;
  tunnelId: number;
}

export interface AhuSectionsResult {
  /** Display order, not API order — see {@link orderInfoBarTunnels}. */
  tunnels: IAhuTunnel[] | undefined;
  /** null while loading — the `.full-ahu-preloader` stays `.active`. */
  sections: AhuSectionLine[] | null;
}

/**
 * The order the info bar lists an AHU's tunnels in, for both the
 * "Tunnel {address} Dimensions" rows and the "Sections" pane.
 *
 * `GET /ahu/tunnels/?ahu_id=` answers in tunnel-id order
 * (`AHUTunnels.Meta.ordering = ['id']`), i.e. creation order, and both panes
 * used to render it as-is. On a HYBRID AHU that is not the address order:
 * inserting a section mid-chain renumbers the downstream tunnels' `position`
 * but leaves their lower ids, so a Heat Wheel inserted at 1:1:4 listed after
 * 1:1:8, and a Plenum added at the head of row 2 listed after 2:1:6.
 *
 * So a hybrid AHU is sorted numerically by row → level → position with the
 * board's own {@link compareTunnelAddress} (numeric per field, so position 10
 * follows 9 rather than sorting between 1 and 2). This is the geometric
 * address order the AHU tab's tunnel list and the tunnel-index pill strip
 * already use — deliberately NOT airflow order, which on a hybrid row can run
 * right to left.
 *
 * A standard AHU keeps the API's id order untouched (same array, not a copy).
 */
export function orderInfoBarTunnels<T extends IAhuTunnel>(
  tunnels: T[] | undefined,
  isHybrid: boolean | undefined
): T[] | undefined {
  if (!tunnels || !isHybrid) return tunnels;
  return [...tunnels].sort(compareTunnelAddress);
}

/**
 * Info-bar sections pipeline (viewProjectAhuUnit.getAhuSections, B1.4 /
 * parity 143): GET /ahu/tunnels/?ahu_id={id}, then one
 * GET /ahu/sections/?tunnel_id={id} per tunnel; accumulate
 * {index, name, tag} (rows without a name skipped), sort by tunnel (see
 * {@link orderInfoBarTunnels} — legacy sorted on the raw tunnel id), then the
 * preloader deactivates. Per-tunnel fetches go through
 * the RTK cache (`initiate`) so re-selecting an AHU reuses cached data.
 *
 * [NEG] honored: no trailing `fn['get.all.cms.data']` (undefined key no-op).
 */
export function useAhuSections(
  ahuId: number | null,
  isHybrid: boolean | undefined
): AhuSectionsResult {
  const dispatch = useAppDispatch();
  const { data: tunnels } = useGetAhuTunnelsQuery(ahuId ?? skipToken);
  const [sections, setSections] = useState<AhuSectionLine[] | null>(null);

  // Memoized so the effect below re-runs exactly as often as it did on the
  // raw `tunnels` reference (every refetch, and only then).
  const orderedTunnels = useMemo(
    () => orderInfoBarTunnels(tunnels, isHybrid),
    [tunnels, isHybrid]
  );

  useEffect(() => {
    setSections(null);
    if (!orderedTunnels || ahuId == null) return undefined;
    let cancelled = false;
    const subs = orderedTunnels.map((t) =>
      dispatch(projectBoardApi.endpoints.getTunnelSections.initiate(t.id))
    );
    Promise.all(subs.map((s) => s.unwrap()))
      .then((results) => {
        if (cancelled) return;
        const acc: AhuSectionLine[] = [];
        results.forEach((rows, i) => {
          const tunnel = orderedTunnels[i];
          rows.forEach((row) => {
            if (row.name === undefined) return;
            acc.push({
              index: ahuTunnelAddress(tunnel, isHybrid),
              name: row.name,
              // Legacy quirk kept bug-for-bug: project_ahu_unit.js prefixes
              // `: ` TWICE (mutates a.tag on line 150, prefixes again on 152),
              // so the live render is "{name} : : {tag}".
              tag: row.tag ? `: : ${row.tag}` : '',
              tunnelId: row.tunnel_id,
            });
          });
        });
        // Sorted on the tunnel's place in `orderedTunnels` rather than on its
        // id, so the pane and the dimensions rows above it list tunnels in the
        // same order. For a standard AHU that place IS the id order, so this
        // is the old `a.tunnelId - b.tunnelId` unchanged. `Array.sort` is
        // stable and each tunnel's rows arrive ordered by `order_rate`
        // (`AHUSection.Meta.ordering`), so section order inside a tunnel is
        // untouched either way.
        const place = new Map(orderedTunnels.map((t, i) => [t.id, i]));
        acc.sort(
          (a, b) => (place.get(a.tunnelId) ?? 0) - (place.get(b.tunnelId) ?? 0)
        );
        setSections(acc);
      })
      .catch(() => {
        if (!cancelled) setSections([]);
      });
    return () => {
      cancelled = true;
      subs.forEach((s) => s.unsubscribe());
    };
  }, [orderedTunnels, ahuId, isHybrid, dispatch]);

  return { tunnels: orderedTunnels, sections };
}
